import React, { useEffect, useRef, useState, useMemo } from "react";
import { Checkbox, Dropdown, Form, Input, Label, Radio, Search, TextArea, Button, Icon } from "semantic-ui-react";
import CurrencyInput from "react-currency-input-field";
import InputMask from "react-input-mask";
import BCToolTip from "../../ui/BCToolTip";
import { GlobalConstants } from "../../constants/GlobalConstants";
import BCPlaces from "../../ui/BCPlaces";
import { convertToDollar, convertToMinFraction } from "../../../Helpers";
import "../../../styles/ui.scss";
import "../../../styles/lms/lms-global.scss";
import "../../../styles/lms/lms-form.scss";
import LmsValidationMessage from "./LmsValidationMessage";
import { useHistory } from "react-router-dom";
import { removeTimestamp, addTimestamp, downloadFile, parsePercentValue, isEmptyValue, parseCurrencyValue } from "../../../helpers/GlobalHelper";
import { css } from "@emotion/css";
import LmsMessage from "./LmsMessage";
import BCModal from "../../ui/BCModal";

const LMSFormInput = ({ ...props }) => {
  const searchRef = useRef() as any;
  const [isSearchOpen, setIsSearchOpen] = useState(true as boolean);
  const [isInFocus, setIsInFocus] = useState(false);
  const inputPlaceholder = props.placeholder ? props.placeholder : null;
  const history = useHistory();
  const [tableErrorInfo, setTableErrorInfo] = useState({ isVisible: false } as any);
  const [mouseOverErrorInfo, setIsMouseOverErrorInfo] = useState({ width: 0, displayOnLeft: false } as any);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isMouseOverDropdown, setIsMouseOverDropdown] = useState(false);
  const [timer, setTimer] = useState(null as NodeJS.Timer | null);
  const [dropdownIsClearable, setDropdownIsClearable] = useState(!!props.clearable);
  const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
  const fileDownload = props.fileDownloads ? props.fileDownloads[props.name] : {};
  const useFormInstance = props.useFormInstance || {};
  const fieldOptions = { ...useFormInstance, ...props };
  const isPercentage = [GlobalConstants.InputTypes.PERCENT, GlobalConstants.InputTypes.PERCENTAGE].indexOf(props.fieldType) > -1;

  const inputValue = useMemo(() => {
    const value = props.value ? props.value : props.value === 0 ? Number(0) : "";
    if (!isEmptyValue(value)) {
      if (isPercentage) {
        return isInFocus ? value : parsePercentValue(value)?.toFixed(props.decimalsLimit || 3)?.toString();
      }
      else if (props.fieldType === "dollar" || props.fieldType === "float") {
        return isInFocus ? value : parseCurrencyValue(value)?.toFixed(props.decimalsLimit || 2);
      }
      else {
        return value;
      }
    }

    return value;
  }, [props.value, isInFocus]);

  const handleCalculateCharactersRemaining = (value, limit) => {
    if (limit) {
      return limit - value.length;
    }
  };

  const handleActionClick = () => {
    if (props.modalContent) {
      setIsAddFormModalOpen(true);
    }
    else if (props.actionPath) {
      history.push(props.actionPath);
    }
    else if (props.actionFunc) {
      props.actionFunc(fieldOptions);
    }
  };

  const handleOnBlur = (e) => {
    if (!isEmptyValue(e.currentTarget.value)) {
      if (isPercentage) {
        const maxValue = props.maxValue || props.maxPercentage ? props.maxValue || props.maxPercentage : 999.999;
        const parsedValue = parsePercentValue(e.currentTarget.value, maxValue);
        props?.handleFormChange(null, { name: props.name, value: parsedValue });
      }

      if (props.fieldType === "dollar" || props.fieldType === "float" || props.maxValue) {
        // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
        const maxValue = props.maxValue ? props.maxValue : props.fieldType !== "float" ? 99999999999999.99 : undefined;
        const parsedValue = parseCurrencyValue(e.currentTarget.value, maxValue);
        props?.handleFormChange(null, { name: props.name, value: parsedValue });
      }
    }

    if (props.onBlur) {
      props.onBlur(props.name, e.currentTarget.value, props?.useFormInstance);
    }

    setIsInFocus(false);
  };

  const handleOnFocus = () => {
    setIsInFocus(true);
  };

  const selectAllDropdown = () => {
    props?.handleFormChange(null, { name: props?.name, value: props?.dropdownOptions?.map(o => o.value) });
  };

  useEffect(() => {
    const handleOutsideSearchClick = (e) => {
      if (searchRef.current && searchRef.current.contains(e.target)) {
        setIsSearchOpen(true);
        return;
      }
  
      setIsSearchOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideSearchClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideSearchClick);
    };
  }, [searchRef]);

  const handleMouseOver = (e, isEnter) => {
    if (isEnter) {
      const parentField = e.currentTarget.parentElement;
      const computedParentWidth = window.getComputedStyle(parentField).getPropertyValue("width");
      const rect = parentField.getBoundingClientRect();
      const screenWidth = window.innerWidth || document.documentElement.clientWidth;

      if ((screenWidth - rect.right) < 350 && rect.left > 350) {
        setIsMouseOverErrorInfo({ width: parseInt(computedParentWidth) + 10, displayOnLeft: true });
      }
      else {
        setIsMouseOverErrorInfo({ width: parseInt(computedParentWidth) + 15, displayOnLeft: false });
      }

      setIsMouseOver(true);
    } else {
      setIsMouseOver(false);
    }
  };

  const actionIsHidden = () => {
    if (props.isActionHidden) {
      return true;
    }
    else if (props.actionHasDependency) {
      return !props.dependencyFieldValue;
    }

    return false;
  };

  const handleRemoveClick = () => {
    if (props.removeActionFunc) {
      props.removeActionFunc(fieldOptions);
    }
    else {
      if (props.useFormInstance.removeInput) {
        props.useFormInstance.removeInput(props.name, props.nestedInputsProp);
      }
    }
  };

  const removeActionIsHidden = () => {
    if (!inputValue) {
      return true;
    }

    return !(!!props.removeActionFunc || props.displayRemoveButton);
  };

  useEffect(() => {
    if (isMouseOver) {
      if (!timer) {
        setTimer(setTimeout(() => {
          setTableErrorInfo({ message: "Error text", isVisible: true });
        }, 400));
      }
    } else {
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
        setTableErrorInfo({ ...tableErrorInfo, isVisible: false });
      }
    }
  }, [isMouseOver]);

  let overwriteErrorMessagePosition = "";
  const mouseOverErrorInfoWidth = mouseOverErrorInfo?.width;

  if (mouseOverErrorInfoWidth) {
    if (mouseOverErrorInfo.displayOnLeft) {
      overwriteErrorMessagePosition = css`
        right: ${mouseOverErrorInfoWidth}px
      `;
    }
    else {
      overwriteErrorMessagePosition = css`
        left: ${mouseOverErrorInfoWidth}px
      `;
    }
  }

  return (
    <React.Fragment>
      {props.type === "plainText" ? <div className="input-plain-text">
        {props.fieldType === "percent" ? `${convertToMinFraction(inputValue, props.decimalsLimit || 3, 3)}%` :
        props.fieldType === "dollar" ? convertToDollar(inputValue) : inputValue}
      </div> : <Form.Field
        width={props.width}
        inline={!!props.inline}
        className={`${(props.type === "toggle" || props.type === "checkbox") ? "primary-toggle-field" : ""} ${props.fieldClass}`}
        required={props.required}
        error={!!props.error}
      >
        {(props.actionLabel && props.actionText && props.labelAction) && <label className={props.labelClass ? props.labelClass : "form-input-label"}>
          <span>{props.actionLabel}</span>
          <span>{props.tooltip && <BCToolTip tooltip={props.tooltip} />}</span>
          <span 
            className={`primary-action-label ${props.actionLabelClass}`} 
            onClick={() => props.labelAction()}
            style={props.setColor ? props.setColor : void 0}
          >{props.actionText}</span>
        </label>}
        {props.label && <label className={props.labelClass ? props.labelClass : "lms-form-input-label"} style={{ color: !props.useFormInstance.isDisabled && props.labelColor ? props.labelColor : "" }}>
          {props.labelIcon}
          {props.label}
          {props.labelWarningText && <span className="lms-form-input-label-warning-text">{props.labelWarningText}</span>}
          {props.tooltip && <BCToolTip tooltip={props.tooltip} />}
        </label>}
        {props.subLabel && <label className="lms-form-input-subLabel">
          {props.subLabel}
        </label>}
        {props.type === "input" ? <React.Fragment>
          <Input
            autoFocus={props.autoFocus}
            className={props.fieldType === "file" ? "lms-file-input-field-container" : props.inputClass}
            labelPosition={props.fieldType === "percent" ? "right" : "left"}
            name={props.name}
            placeholder={inputPlaceholder}
            onChange={props.handleFormChange}
            type={props.fieldType === "password" ? "password" : props.fieldType === "dollar" || props.fieldType === "number" ? "number" : props.inputType ? props.inputType : props.fieldType === "file" ? "file" : "text"}
            disabled={!!props.actionFunc || !!props.actionPath ? false : props.disabled}
            onBlur={handleOnBlur}
            action={(!!props.actionFunc || !!props.actionPath || !!props.modalContent)}
          >
            {(props.fieldType === "dollar" || props.unitType === "$") && <Label basic>$</Label>}
            {props.fieldType === "socialSecurityHidden" ? <InputMask 
              value={inputValue} 
              mask="xxx-xx-9999"
              maskChar=" "
            /> : props.fieldType === "socialSecurity" ? <InputMask 
              placeholder={inputPlaceholder}
              value={inputValue}
              mask="999-99-9999"
              maskChar=" "
              onChange={(e) => props.handleFormChange(null, {value: e.target.value, name: props.name})}
            /> : props.fieldType === "phone" ? <InputMask
              placeholder="e.g. (206) 555-1234"
              mask="(999) 999-9999" 
              maskChar=" "
              onChange={(e) => props.handleFormChange(null, {value: e.target.value?.replace(/\D+/g, ""), name: props.name})}
              value={inputValue}
              disabled={props.disabled}
            /> : props.fieldType === GlobalConstants.InputTypes.YEAR_MONTH ? <InputMask
              placeholder="e.g. 1999-11"
              mask="9999-99" 
              maskChar=" "
              onChange={(e) => props.handleFormChange(null, {value: e.target.value, name: props.name})}
              value={inputValue}
              disabled={props.disabled}
            /> : props.fieldType === GlobalConstants.InputTypes.NUMBER ? <InputMask
              placeholder={props.placeholder}
              onChange={(e) => props.handleFormChange(null, {value: e.target.value?.replace(/\D+/g, ""), name: props.name})}
              value={inputValue}
              disabled={props.disabled}
              onBlur={handleOnBlur}
            /> : props.fieldType === "hidden" ? <input 
              className="hidden-input" 
              value="Hidden" 
            /> : props.fieldType === "dollar" || isPercentage || props.fieldType === "float" || props.fieldType === "int" ? <CurrencyInput
              className="primary-currency-input"
              style={{color: props.allowNegativeValue && inputValue < 0 ? "red" : ""}}
              allowDecimals={props.fieldType !== "int"}
              decimalsLimit={props.decimalsLimit ? props.decimalsLimit : isPercentage ? 3 : 2}
              decimalScale={isInFocus || props.fieldType === "int" ? undefined : props.decimalScale ? props.decimalScale : isPercentage ? props.decimalsLimit || 3 : props.decimalsLimit || 2}
              disableGroupSeparators={["int", "float", "number"].includes(props.fieldType) || isPercentage}
              placeholder={inputPlaceholder ? inputPlaceholder : undefined}
              value={inputValue}
              name={props.name}
              onValueChange={(value) => props.handleFormChange(null, {value: value, name: props.name}, props.fieldType === "int" ? "number" : "")}
              onBlur={handleOnBlur}
              onFocus={handleOnFocus}
              disabled={props.disabled}
              allowNegativeValue={!!props.allowNegativeValue}
              defaultValue={!isEmptyValue(props.defaultValue) ? props.defaultValue : undefined}
            /> : <input disabled={props.disabled} className={props.inputClass ? props.inputClass : ""} value={inputValue} data-lpignore="true" />}
            {props.fieldType === "percent" && <Label basic>%</Label>}
            {(props.unitType && props.unitType !== "$" && isPercentage) && <Label basic className={props.unitTypeClass}>{props.unitType}</Label>}
            {(props.actionFunc || props.actionPath || props.modalContent) && (
              <Button icon={props.actionIcon} disabled={props?.disabled && !props.isActionEnabled} size="mini" onClick={() => handleActionClick()} content={props.actionLabel || ""} />
            )}
            {(props.fieldType === "file" && fileDownload?.fileName) && <>
              <a className="lms-download-form-field-link" onClick={() => {
                return downloadFile(fileDownload.documentDownloadUrl, { fileName: fileDownload.fileName, id: props?.useFormInstance?.inputs?.id });
              }}>{fileDownload.fileName}</a>
            </>}
          </Input>
          {props.charLimit && <span className="primary-textarea-limit">{handleCalculateCharactersRemaining(inputValue, props.charLimit)} Characters Remaining</span>}
        </React.Fragment> : props.type === "socialSecurity" ? <Input
          labelPosition="left"
          disabled={props.disabled}
        >
          {(props.actionFunc || props.actionPath || props.modalContent) && (
            <Button icon={props.actionIcon} disabled={props?.disabled} size="mini" onClick={() => handleActionClick()}>{props.actionLabel}</Button>
          )}
        </Input> : props.type === "radio" ? <React.Fragment>
                {props.inputArray && props.inputArray.map((item, i) => {
                let valueField = item;
                if (item[props.valueField] || item[props.valueField] === false) {
                  valueField = item[props.valueField];
                }
                
                let valueFieldDisplay = undefined as string | undefined;
                if (typeof valueField === "boolean") {
                  valueFieldDisplay = valueField.toString();
                }
    
                let itemLabel = item;
                if (item.name) {
                  itemLabel = `${item.name}${item.description ? ` (${item.description})` : ""}`;
                }
                return (
                  <Form.Field key={`${props.field}-radio-${i}`}>
                    <Radio
                      className="primary-radio"
                      label={itemLabel}
                      value={props.value ? props.value : valueFieldDisplay ? valueFieldDisplay : valueField}
                      checked={props.obj[props.field] === valueField}
                      onChange={() => props.handleFormChange(valueField, props.field)}
                      disabled={props.disabled}
                    />
                  </Form.Field>
                );
              })}
        </React.Fragment> : props.type === "dropdown" ? (
          <div className={(props.actionFunc || props.actionPath || props.modalContent || props.dropdownMultiple || !removeActionIsHidden()) ? "lms-dropdown-field-container" : "lms-full-width"}>
            <div
              className={!props.dropdownMultiple ? "lms-dropdown-field-clearable-wrapper" : ""}
              onMouseEnter={() => {
                setIsMouseOverDropdown(true);
              }}
              onMouseLeave={() => {
                setIsMouseOverDropdown(false);
              }}
            >
              <Dropdown
                className={props.inputDropdownClass ? props.inputDropdownClass : "primary-dropdown"}
                value={inputValue ? inputValue : props.dropdownMultiple ? [] : null}
                name={props.name}
                placeholder={props.placeholder ? props.placeholder : "Please Select"}
                fluid={props.fluid}
                search
                selection
                multiple={!!props.dropdownMultiple}
                loading={!!props.dropdownLoading}
                error={props.dropdownError}
                options={props.dropdownOptions}
                onChange={props.handleFormChange}
                onSearchChange={props.handleSearchChange}
                searchQuery={props.searchQuery}
                disabled={props.disabled}
                upward={props.upward}
                clearable={props.skipClearable ? false : dropdownIsClearable || props.dropdownMultiple}
                onFocus={() => {
                  if (!props.dropdownMultiple && !props.skipClearable) {
                    setDropdownIsClearable(true);
                  }
                }}
                onBlur={(_, { name, value }) => {
                  if (dropdownIsClearable) {
                    setDropdownIsClearable(false);
                  }
                  if (props.onBlur) {
                    props.onBlur(name, value, props?.useFormInstance);
                  }
                }}
              />
              {inputValue && !props.dropdownMultiple && !dropdownIsClearable && isMouseOverDropdown && !props.disabled && !props.skipClearable && (
                <Icon
                  name="delete"
                  size="small"
                  onClick={() => props.handleFormChange(null, { value: null, name: props.name })} />
              )}
            </div>
            {props.dropdownMultiple && <Button className="dropdown-options-button" onClick={() => selectAllDropdown()} disabled={props?.disabled && !props.isActionEnabled} content={"Select All"} />}
            {(props.actionFunc || props.actionPath || props.modalContent) && !actionIsHidden() && (
              <Button
                size="big"
                disabled={props?.disabled && !props.isActionEnabled}
                className="dropdown-options-button"
                icon={props.actionIcon}
                onClick={() => handleActionClick()}
                content={props.actionLabel}
              />
            )}
            {!removeActionIsHidden() && (
              <Button
                size="big"
                className="dropdown-options-button"
                icon={props.removeActionIcon || "minus circle"}
                onClick={() => handleRemoveClick()}
                content={props.removeActionLabel} />
            )}
          </div>) : props.type === "textarea" ? <React.Fragment>
            <TextArea
              onChange={props.handleFormChange}
              placeholder={inputPlaceholder}
              rows={props.rows ? props.rows : 1}
              value={inputValue}
              disabled={props.disabled}
              name={props.name}
            />
            {props.charLimit && <span className="primary-textarea-limit">{handleCalculateCharactersRemaining(inputValue, props.charLimit)} Characters Remaining</span>}
          </React.Fragment> : props.type === "toggle" ? <React.Fragment>
            <Checkbox 
              className="view-toggle" 
              toggle
              checked={!!props.value}
              onChange={props.handleFormChange}
              defaultChecked={props.defaultToTrue}
              disabled={props.disabled}
              name={props.name}
            />
          </React.Fragment> : props.type === "calendar" ? <React.Fragment>
          <Input
            className={props.inputCalendarClass ? props.inputCalendarClass : ""}
            onChange={(e) => props.handleFormChange(e, {value: addTimestamp(e.target.value), name: props.name})}
            placeholder={inputPlaceholder}
            type="date"
            value={removeTimestamp(inputValue)}
            name={props.name}
            onBlur={handleOnBlur}
            fluid
            disabled={props.disabled}
            min={props.minDate}
            max={props.maxDate}
          />
        </React.Fragment> : props.type === "checkbox" ? <Checkbox
          className={`primary-checkbox ${props.labelClass && props.labelClass}`}
          label={props.checkboxText}
          checked={!!props.value}
          defaultChecked={props.defaultToTrue}
          onChange={() => props.handleFormChange(null, {value: !props.value, name: props.name}, "checkbox")}
          disabled={props.disabled}
        /> : props.type === GlobalConstants.FormInputTypes.PLACES ? <BCPlaces {...props} /> : 
          props.type === GlobalConstants.FormInputTypes.SEARCH_FILTER ? <div ref={searchRef}>
            <Search
              className={props.searchClass ? props.searchClass : "bc-search-input"}
              value={props.value}
              open={props.isSearchOpen && isSearchOpen}
              results={props.renderSuggestions}
              onSearchChange={(e, { value }) => props.onSearchChange(value, props.field)}
              onResultSelect={props.handleSearchSelect}
              placeholder={props.placeholder ? `e.g. ${props.placeholder}` : ""}
              showNoResults
              onFocus={props.onSearchFocus}
              noResultsMessage={props.noSearchResultsMessage}
              onKeyDown={(e) => props.handleSearchKeyDown(e)}
              disabled={props.disabled}
            />
          </div> : null}
        {(!props.isTableInline && !props.isCompact) && <LmsValidationMessage error={props.error} label={props.errorLabel || props.label} hideErrorMessage={props.hideErrorMessage} />}
        {props.isTableInline && <React.Fragment>
          {(props.error) && <span
            className="info-squared-icon"
            onMouseEnter={(e) => handleMouseOver(e, true)}
            onMouseLeave={(e) => handleMouseOver(e, false)}
          >
          <div><Icon name={GlobalConstants.Icons.INFO as any}/></div>
        </span>}
          <LmsMessage
            compact
            skipDismiss
            skipHeader
            messageStyles={`error-info-table-message ${overwriteErrorMessagePosition}`}
            text={props?.error?.allDetailedErrors}
            visible={tableErrorInfo.isVisible}
            indentLines
            duration={500}
          />
        </React.Fragment>}
        {props.modalContent && <BCModal
          isModalOpen={isAddFormModalOpen}
          size={props.modalSize || "large"}
          closeIcon
          onClose={() => setIsAddFormModalOpen(false)}
          content={props.modalContent ? props.modalContent(setIsAddFormModalOpen, { ...props }) : null}
        />}
      </Form.Field>}
    </React.Fragment>
  );
};

export default LMSFormInput;